import { randomUUID } from "node:crypto";
import type { TokenCredential } from "@azure/identity";
import { authedJson, authedJsonResult } from "./http.js";
import { LocalCliError } from "./errors.js";

const ARM = "https://management.azure.com";
const ARM_SCOPE = "https://management.azure.com/.default";
const RA_API = "2022-04-01";

export const FOUNDRY_USER_ROLE_ID = "53ca6127-db72-4b80-b1b0-d745d6d5456d";
export const ACR_PULL_ROLE_ID = "7f951dda-4ed3-4680-a7ca-43fe172d538d";
const OWNER_ROLE_ID = "8e3af657-a8ff-443c-a75c-2fe8c4bcb635";
export const USER_ACCESS_ADMIN_ROLE_ID = "18d7d88d-d35e-4fb5-a5c3-7773c20a72d9";
const RBAC_ADMIN_ROLE_ID = "f58310d9-a9f6-439a-9e8d-f62e7b41a168";
export const STORAGE_TABLE_DATA_CONTRIBUTOR_ROLE_ID = "0a9a7e1f-b9d0-4cc4-a60d-0319b160aaa3";

const ASSIGNING_ROLE_IDS = [OWNER_ROLE_ID, USER_ACCESS_ADMIN_ROLE_ID, RBAC_ADMIN_ROLE_ID];

interface RoleAssignmentList {
  value?: { properties?: { roleDefinitionId?: string; principalId?: string } }[];
}

/** Grant the Foundry User role to a principal at the given (account) scope. Idempotent — 409 = already granted. */
export async function grantFoundryUser(args: {
  credential: TokenCredential;
  subscriptionId: string;
  accountScope: string;
  principalId: string;
  /** Defaults to "ServicePrincipal". Pass "User" when granting to a human AAD user OID. */
  principalType?: string;
}): Promise<void> {
  const roleAssignmentId = randomUUID();
  const url = `${ARM}${args.accountScope}/providers/Microsoft.Authorization/roleAssignments/${roleAssignmentId}?api-version=${RA_API}`;
  await authedJsonResult({
    credential: args.credential,
    scope: ARM_SCOPE,
    method: "PUT",
    url,
    body: {
      properties: {
        roleDefinitionId: `/subscriptions/${args.subscriptionId}/providers/Microsoft.Authorization/roleDefinitions/${FOUNDRY_USER_ROLE_ID}`,
        principalId: args.principalId,
        principalType: args.principalType ?? "ServicePrincipal",
      },
    },
    okStatuses: [409], // RoleAssignmentExists — already granted
  });
}

/** Grant Storage Table Data Contributor at `scope` (a storage-account resource id). Idempotent. */
export async function grantStorageTableDataContributor(args: {
  credential: TokenCredential;
  subscriptionId: string;
  scope: string;
  principalId: string;
  principalType?: string;
}): Promise<void> {
  const roleAssignmentId = randomUUID();
  const url = `${ARM}${args.scope}/providers/Microsoft.Authorization/roleAssignments/${roleAssignmentId}?api-version=${RA_API}`;
  await authedJsonResult({
    credential: args.credential,
    scope: ARM_SCOPE,
    method: "PUT",
    url,
    body: {
      properties: {
        roleDefinitionId: `/subscriptions/${args.subscriptionId}/providers/Microsoft.Authorization/roleDefinitions/${STORAGE_TABLE_DATA_CONTRIBUTOR_ROLE_ID}`,
        principalId: args.principalId,
        principalType: args.principalType ?? "User",
      },
    },
    okStatuses: [409],
  });
}

async function listRoleAssignments(
  credential: TokenCredential,
  scope: string,
  filter?: string,
): Promise<RoleAssignmentList> {
  const q = `api-version=${RA_API}${filter ? `&$filter=${encodeURIComponent(filter)}` : ""}`;
  const url = `${ARM}${scope}/providers/Microsoft.Authorization/roleAssignments?${q}`;
  return (await authedJson<RoleAssignmentList>({ credential, scope: ARM_SCOPE, method: "GET", url })) ?? {};
}

function roleIdMatches(roleDefinitionId: string | undefined, roleGuid: string): boolean {
  return (roleDefinitionId ?? "").toLowerCase().endsWith(roleGuid.toLowerCase());
}

/** True if `principalId` holds AcrPull at the ACR scope. */
export async function principalHasAcrPull(args: {
  credential: TokenCredential;
  acrScope: string;
  principalId: string;
}): Promise<boolean> {
  const list = await listRoleAssignments(args.credential, args.acrScope);
  return (list.value ?? []).some(
    (a) =>
      a.properties?.principalId === args.principalId &&
      roleIdMatches(a.properties.roleDefinitionId, ACR_PULL_ROLE_ID),
  );
}

/** Grant AcrPull to a principal at the ACR scope. Idempotent. */
export async function grantAcrPull(args: {
  credential: TokenCredential;
  subscriptionId: string;
  acrScope: string;
  principalId: string;
}): Promise<void> {
  const url = `${ARM}${args.acrScope}/providers/Microsoft.Authorization/roleAssignments/${randomUUID()}?api-version=${RA_API}`;
  await authedJsonResult({
    credential: args.credential,
    scope: ARM_SCOPE,
    method: "PUT",
    url,
    body: {
      properties: {
        roleDefinitionId: `/subscriptions/${args.subscriptionId}/providers/Microsoft.Authorization/roleDefinitions/${ACR_PULL_ROLE_ID}`,
        principalId: args.principalId,
        principalType: "ServicePrincipal",
      },
    },
    okStatuses: [409],
  });
}

/** True if the caller (by object id) holds Owner / UAA / RBAC Admin at or above `scope`. */
export async function callerCanAssignRoles(args: {
  credential: TokenCredential;
  scope: string;
  callerObjectId: string;
}): Promise<boolean> {
  // $filter=atScope() returns assignments at and above the scope; principalId filter
  // includes group-inherited assignments via assignedTo().
  const list = await listRoleAssignments(
    args.credential,
    args.scope,
    `atScope() and assignedTo('${args.callerObjectId}')`,
  );
  return (list.value ?? []).some((a) =>
    ASSIGNING_ROLE_IDS.some((g) => roleIdMatches(a.properties?.roleDefinitionId, g)),
  );
}

export const KV_SECRETS_USER_ROLE_ID = "4633458b-17de-408a-b874-0445c86b69e6";

/** Grant Key Vault Secrets User to a principal at the KV resource scope. Idempotent — 409 = already granted. */
export async function grantKeyVaultSecretsUser(args: {
  credential: TokenCredential;
  subscriptionId: string;
  kvScope: string; // /subscriptions/.../providers/Microsoft.KeyVault/vaults/<name>
  principalId: string;
}): Promise<void> {
  const url = `${ARM}${args.kvScope}/providers/Microsoft.Authorization/roleAssignments/${randomUUID()}?api-version=${RA_API}`;
  await authedJsonResult({
    credential: args.credential,
    scope: ARM_SCOPE,
    method: "PUT",
    url,
    body: {
      properties: {
        roleDefinitionId: `/subscriptions/${args.subscriptionId}/providers/Microsoft.Authorization/roleDefinitions/${KV_SECRETS_USER_ROLE_ID}`,
        principalId: args.principalId,
        principalType: "ServicePrincipal",
      },
    },
    okStatuses: [409],
  });
}

/** True if `principalId` holds Key Vault Secrets User at the KV resource scope. */
export async function principalHasKvSecretsUser(args: {
  credential: TokenCredential;
  kvScope: string;
  principalId: string;
}): Promise<boolean> {
  const list = await listRoleAssignments(args.credential, args.kvScope);
  return (list.value ?? []).some(
    (a) =>
      a.properties?.principalId === args.principalId &&
      roleIdMatches(a.properties.roleDefinitionId, KV_SECRETS_USER_ROLE_ID),
  );
}

export const CONTRIBUTOR_ROLE_ID = "b24988ac-6180-42a0-ab88-20f7382dd24c";

/** Grant the Contributor role to a principal at the given scope (RG or subscription). Idempotent — 409 = already granted. */
export async function grantContributor(args: {
  credential: TokenCredential;
  subscriptionId: string;
  scope: string; // full ARM scope id: /subscriptions/<id>[/resourceGroups/<rg>]
  principalId: string;
}): Promise<void> {
  const url = `${ARM}${args.scope}/providers/Microsoft.Authorization/roleAssignments/${randomUUID()}?api-version=${RA_API}`;
  await authedJsonResult({
    credential: args.credential,
    scope: ARM_SCOPE,
    method: "PUT",
    url,
    body: {
      properties: {
        roleDefinitionId: `/subscriptions/${args.subscriptionId}/providers/Microsoft.Authorization/roleDefinitions/${CONTRIBUTOR_ROLE_ID}`,
        principalId: args.principalId,
        principalType: "ServicePrincipal",
      },
    },
    okStatuses: [409],
  });
}

/** Grant User Access Administrator to a principal at the given scope (RG or subscription). Idempotent — 409 = already granted. */
export async function grantUserAccessAdmin(args: {
  credential: TokenCredential;
  subscriptionId: string;
  scope: string; // full ARM scope id: /subscriptions/<id>[/resourceGroups/<rg>]
  principalId: string;
}): Promise<void> {
  const url = `${ARM}${args.scope}/providers/Microsoft.Authorization/roleAssignments/${randomUUID()}?api-version=${RA_API}`;
  await authedJsonResult({
    credential: args.credential,
    scope: ARM_SCOPE,
    method: "PUT",
    url,
    body: {
      properties: {
        roleDefinitionId: `/subscriptions/${args.subscriptionId}/providers/Microsoft.Authorization/roleDefinitions/${USER_ACCESS_ADMIN_ROLE_ID}`,
        principalId: args.principalId,
        principalType: "ServicePrincipal",
      },
    },
    okStatuses: [409],
  });
}

/** Resolve a Key Vault's ARM resource id from its vault URI, via ARM resources list. */
export async function resolveKeyVaultResourceId(args: {
  credential: TokenCredential;
  subscriptionId: string;
  kvUri: string;
}): Promise<string> {
  const host = new URL(args.kvUri).host; // m8t-kv-xxx.vault.azure.net
  const name = host.split(".")[0];
  const url = `${ARM}/subscriptions/${args.subscriptionId}/resources?api-version=2021-04-01&$filter=${encodeURIComponent(`resourceType eq 'Microsoft.KeyVault/vaults' and name eq '${name}'`)}`;
  const data = await authedJson<{ value?: { id?: string }[] }>({
    credential: args.credential,
    scope: ARM_SCOPE,
    method: "GET",
    url,
  });
  const id = data?.value?.[0]?.id;
  if (!id) {
    throw new LocalCliError({
      code: "KV_RESOURCE_NOT_FOUND",
      message: `Could not resolve the ARM resource id for Key Vault '${name}' in subscription ${args.subscriptionId}.`,
    });
  }
  return id;
}
